import { useEffect, useState } from "react";
import SelectionModal from "../../../components/common/modalPopup/selectionModal";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

const radioOptions = [
  { label: "7 Days", value: "7" },
  { label: "15 Days", value: "15" },
  { label: "21 Days", value: "21" },
  { label: "30 Days", value: "30" },
  { label: "Custom", value: "custom" },
];

interface UserActivationPromptProps {
  isOpen: boolean;
  closeUserActivationModal: CallableFunction;
  table: any;
  asyncOnSubimtFunction: CallableFunction;
}

const UserActivationPrompt = ({
  isOpen,
  closeUserActivationModal,
  table,
  asyncOnSubimtFunction,
}: UserActivationPromptProps) => {
  // ***** States *****
  const [disableSubmitButton, setDisableSubmitButton] = useState(true);
  const [isModalOpen, setModalOpen] = useState(isOpen);
  const [isDaysActivationSelectionOpen, setDaysActivationSelection] =
    useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<string>("7"); // default selection
  const [customDays, setCustomDays] = useState<string>("");

  // ***** Use Effects *****
  useEffect(() => {
    const isCustomDaysValid =
      customDays && !isNaN(Number(customDays)) && Number(customDays) > 0;
    const isSelectedDaysValid =
      selectedDays !== "custom" && !isNaN(Number(selectedDays));
    // conditionally enable and disable the submit button below.
    isSelectedDaysValid || (selectedDays === "custom" && isCustomDaysValid)
      ? setDisableSubmitButton(false)
      : setDisableSubmitButton(true);
  }, [selectedDays, customDays]);

  // ***** Handlers *****
  const handleAsycSubmitOperation = async () => {
    if (!isDaysActivationSelectionOpen) {
      setDaysActivationSelection(true);
      return;
    }
    const userSelectedDays =
      selectedDays !== "custom" ? selectedDays : customDays;
    await asyncOnSubimtFunction(table, false, userSelectedDays);
    handleClose();
  };

  const handleClose = () => {
    setDaysActivationSelection(false);
    setModalOpen(false);
    closeUserActivationModal();
  };

  const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDays(event.target.value);
  };

  const handleCustomDaysChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomDays(event.target.value);
  };

  return (
    <>
      <SelectionModal
        onClose={handleClose}
        asyncOperation={handleAsycSubmitOperation}
        open={isModalOpen}
        title={"Unuploaded Files Warning"}
        showCancelOption={true}
        submitButtonName={isDaysActivationSelectionOpen ? "Submit" : "Yes"}
        disableSubmit={disableSubmitButton}
      >
        <Box>
          {!isDaysActivationSelectionOpen && (
            <Typography>
              Some of the selected users have missing documents. Do you still
              wish to active?
            </Typography>
          )}
          {isDaysActivationSelectionOpen && (
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12}>
                <Typography>
                  Please specify the number of active days.
                </Typography>
                <RadioGroup
                  value={selectedDays}
                  onChange={handleDaysChange}
                  row
                >
                  {radioOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </Grid>
              {selectedDays === "custom" && (
                <Grid item xs={6}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Custom Days"
                    value={customDays}
                    onChange={handleCustomDaysChange}
                  />
                </Grid>
              )}
            </Grid>
          )}
          {isDaysActivationSelectionOpen && (
            <Typography mb={-2} mt={2}>
              <b>Note: </b>Users will automatically become inactive after the
              specified number of days if mandatory documents are not provided.
            </Typography>
          )}
        </Box>
      </SelectionModal>
    </>
  );
};

export default UserActivationPrompt;
